import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * S4: Deterministic Financial Dashboard API
 * Returns financial data EXCLUSIVELY from reporting tables (never Cegid direct).
 * Supports: Month, Quarter, Year, Custom range filters.
 * 
 * asks.txt: "dashboards are read-only, informational, never modify ERP data"
 * asks.txt: "no AI guessing - deterministic calculations only"
 * 
 * GET /api/dashboard/financial?clienteId=X&filter=month|quarter|year|custom&from=2026-01&to=2026-03
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();
    const { clienteId, filter, from, to, ejercicio } = req.query;

    // Determine period range based on filter type
    let periodoFrom: string;
    let periodoTo: string;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    switch (filter) {
      case 'month': {
        const targetMonth = from ? (from as string) : `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
        periodoFrom = targetMonth;
        periodoTo = targetMonth;
        break;
      }
      case 'quarter': {
        const quarter = Math.ceil(currentMonth / 3);
        const qStart = (quarter - 1) * 3 + 1;
        const qEnd = quarter * 3;
        const yr = ejercicio || String(currentYear);
        periodoFrom = `${yr}-${String(qStart).padStart(2, '0')}`;
        periodoTo = `${yr}-${String(qEnd).padStart(2, '0')}`;
        break;
      }
      case 'year': {
        const yr = ejercicio || String(currentYear);
        periodoFrom = `${yr}-01`;
        periodoTo = `${yr}-12`;
        break;
      }
      case 'custom': {
        periodoFrom = (from as string) || `${currentYear}-01`;
        periodoTo = (to as string) || `${currentYear}-12`;
        break;
      }
      default: {
        // Default: current year
        periodoFrom = `${currentYear}-01`;
        periodoTo = `${currentYear}-12`;
      }
    }

    // Build query
    let query = supabaseAdmin
      .from('tax_summary')
      .select('*')
      .gte('periodo', periodoFrom)
      .lte('periodo', periodoTo)
      .order('periodo', { ascending: true });

    if (clienteId) {
      query = query.eq('cliente_id', clienteId as string);
    }

    const { data: summaries, error } = await query;
    if (error) throw error;

    const items = summaries || [];

    // Aggregate totals
    const totals = {
      ingresos: items.reduce((sum: number, s: any) => sum + (Number(s.base_imponible_emitidas) || 0), 0),
      gastos: items.reduce((sum: number, s: any) => sum + (Number(s.base_imponible_recibidas) || 0), 0),
      resultado: 0,
      iva_repercutido: items.reduce((sum: number, s: any) => sum + (Number(s.iva_repercutido) || 0), 0),
      iva_soportado: items.reduce((sum: number, s: any) => sum + (Number(s.iva_soportado) || 0), 0),
      iva_resultado: items.reduce((sum: number, s: any) => sum + (Number(s.iva_resultado) || 0), 0),
      retenciones_practicadas: items.reduce((sum: number, s: any) => sum + (Number(s.retenciones_practicadas) || 0), 0),
      retenciones_soportadas: items.reduce((sum: number, s: any) => sum + (Number(s.retenciones_soportadas) || 0), 0),
      suplidos: items.reduce((sum: number, s: any) => sum + (Number(s.total_suplidos) || 0), 0),
      facturas_emitidas: items.reduce((sum: number, s: any) => sum + (Number(s.num_facturas_emitidas) || 0), 0),
      facturas_recibidas: items.reduce((sum: number, s: any) => sum + (Number(s.num_facturas_recibidas) || 0), 0),
    };
    totals.resultado = totals.ingresos - totals.gastos;

    // Monthly evolution (for charts)
    const monthlyEvolution = items.map((s: any) => ({
      periodo: s.periodo,
      ingresos: Number(s.base_imponible_emitidas) || 0,
      gastos: Number(s.base_imponible_recibidas) || 0,
      resultado: (Number(s.base_imponible_emitidas) || 0) - (Number(s.base_imponible_recibidas) || 0),
      iva_resultado: Number(s.iva_resultado) || 0,
      facturas_emitidas: Number(s.num_facturas_emitidas) || 0,
      facturas_recibidas: Number(s.num_facturas_recibidas) || 0,
    }));

    // KPIs
    const numMonths = items.length || 1;
    const kpis = {
      margen_neto: totals.ingresos > 0
        ? Math.round((totals.resultado / totals.ingresos) * 10000) / 100
        : 0,
      media_mensual_ingresos: Math.round((totals.ingresos / numMonths) * 100) / 100,
      media_mensual_gastos: Math.round((totals.gastos / numMonths) * 100) / 100,
      ratio_gastos_ingresos: totals.ingresos > 0
        ? Math.round((totals.gastos / totals.ingresos) * 10000) / 100
        : 0,
      iva_neto_a_pagar: totals.iva_resultado,
      total_retenciones: totals.retenciones_practicadas + totals.retenciones_soportadas,
    };

    // Get last sync timestamp
    const lastSyncItem = items.find((s: any) => s.last_sync_at);
    const lastSyncAt = lastSyncItem?.last_sync_at || null;

    return res.status(200).json({
      success: true,
      data: {
        filter: {
          type: filter || 'year',
          from: periodoFrom,
          to: periodoTo,
          clienteId: clienteId || null,
        },
        totals,
        kpis,
        monthly_evolution: monthlyEvolution,
        last_sync_at: lastSyncAt,
        source: 'reporting_tables',
        disclaimer: 'Datos calculados determinísticamente desde las tablas de reporting. No se consulta Cegid directamente.',
      },
    });
  } catch (error: any) {
    console.error('[Financial Dashboard] Error:', error);
    return res.status(500).json({ error: 'Error al obtener datos financieros.' });
  }
}

export default withAuth(handler, ['admin', 'asesor', 'cliente']);
